// Argus — Breadcrumb Windows-style: Back history + Forward history + Up parent + Refresh +
// breadcrumbs separate cu „>" integrate într-o zonă tip TextField outlined.

import type { ReactNode } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import { ArgusToolbar } from '../../components/floating/ArgusToolbar';
import { pathSegments } from '../../lib/fsEntries';

export interface BreadcrumbProps {
  currentPath: string;
  canGoBack: boolean;
  canGoForward: boolean;
  canGoUp: boolean;
  onBack: () => void;
  onForward: () => void;
  onUp: () => void;
  onNavigate: (path: string) => void;
  onRefresh: () => void;
  refreshing?: boolean;
  /** Slot pentru IconButton dropdown menu contextual (Show tree | Show preview | View | Sort by | Options | Properties). */
  trailingMenuSlot?: ReactNode;
}

export function Breadcrumb({
  currentPath,
  canGoBack,
  canGoForward,
  canGoUp,
  onBack,
  onForward,
  onUp,
  onNavigate,
  onRefresh,
  refreshing,
  trailingMenuSlot,
}: BreadcrumbProps): JSX.Element {
  const segments = currentPath === '' ? [] : pathSegments(currentPath);

  return (
    <ArgusToolbar>
      <Tooltip title="Back (history)">
        <span>
          <IconButton size="small" disabled={!canGoBack} onClick={onBack}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Forward (history)">
        <span>
          <IconButton size="small" disabled={!canGoForward} onClick={onForward}>
            <ArrowForwardIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Up (parent folder)">
        <span>
          <IconButton size="small" disabled={!canGoUp} onClick={onUp}>
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      {/* Fix #1: Refresh mutat aici (între Up și breadcrumbs zone). */}
      <Tooltip title={refreshing ? 'Refreshing…' : 'Refresh'}>
        <span>
          <IconButton size="small" disabled={refreshing} onClick={onRefresh}>
            <RefreshIcon fontSize="small" sx={refreshing ? { animation: 'argusPulse 1s infinite' } : {}} />
          </IconButton>
        </span>
      </Tooltip>

      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minHeight: 32,
          px: 1,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        <Box
          onClick={() => onNavigate('')}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
          title="This PC (drives)"
        >
          <StorageIcon sx={{ fontSize: 18 }} />
        </Box>
        {segments.map((seg, idx) => {
          const isDrive = idx === 0 && /^[A-Za-z]:$/.test(seg);
          const target = isDrive
            ? `${seg}\\`
            : segments.slice(0, idx + 1).join('\\');
          return (
            <Box key={`${target}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600, mx: 0.25 }}>›</Typography>
              <Typography
                variant="body2"
                onClick={() => onNavigate(target)}
                sx={{ cursor: 'pointer', fontSize: 13, '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
                noWrap
              >
                {seg}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Fix #4: slot pentru IconButton menu contextual după zona breadcrumbs. */}
      {trailingMenuSlot}
    </ArgusToolbar>
  );
}
